import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { useApiQuery } from '@/hooks/use-api'
import type { BadgeProps } from '@/components/ui/badge'
import type { ImportRowInput } from '@/pages/customers/contact-import-lib'
import { getStages } from '@/lib/lifecycle-stage-cache'
import { i18n } from '@/lib/i18n'

// ── Hằng số & nhãn tiếng Việt ────────────────────────────────────────
export const LIFECYCLE_STAGES = [
  'subscriber',
  'lead',
  'qualified',
  'opportunity',
  'customer',
  'evangelist',
  'churned',
] as const

export type LifecycleStage = (typeof LIFECYCLE_STAGES)[number]

export const STAGE_LABELS: Record<string, string> = {
  get subscriber() { return i18n.t('custList:hook.stage.subscriber') },
  lead: 'Lead',
  get qualified() { return i18n.t('custList:hook.stage.qualified') },
  get opportunity() { return i18n.t('custList:hook.stage.opportunity') },
  get customer() { return i18n.t('custList:hook.stage.customer') },
  get evangelist() { return i18n.t('custList:hook.stage.evangelist') },
  get churned() { return i18n.t('custList:hook.stage.churned') },
}

export function stageLabel(stage?: string | null): string {
  if (!stage) return '—'
  return getStages().find((x) => x.key === stage)?.label ?? STAGE_LABELS[stage] ?? stage
}

export function stageBadgeVariant(stage?: string | null): BadgeProps['variant'] {
  switch (stage) {
    case 'customer':
    case 'evangelist':
      return 'success'
    case 'qualified':
    case 'opportunity':
      return 'warning'
    case 'churned':
      return 'destructive'
    case 'lead':
      return 'default'
    default:
      return 'secondary'
  }
}

export const SENTIMENT_LABELS: Record<string, string> = {
  get positive() { return i18n.t('custList:hook.sentiment.positive') },
  get neutral() { return i18n.t('custList:hook.sentiment.neutral') },
  get negative() { return i18n.t('custList:hook.sentiment.negative') },
}

export function sentimentBadgeVariant(label?: string | null): BadgeProps['variant'] {
  switch (label) {
    case 'positive':
      return 'success'
    case 'negative':
      return 'destructive'
    default:
      return 'secondary'
  }
}

export const INTENT_LABELS: Record<string, string> = {
  get hot() { return i18n.t('custList:hook.intent.hot') },
  get warm() { return i18n.t('custList:hook.intent.warm') },
  get cold() { return i18n.t('custList:hook.intent.cold') },
}

export function intentBadgeVariant(intent?: string | null): BadgeProps['variant'] {
  switch (intent) {
    case 'hot':
      return 'destructive'
    case 'warm':
      return 'warning'
    default:
      return 'secondary'
  }
}

// ── Kiểu dữ liệu ─────────────────────────────────────────────────────
export interface ContactListItem {
  id: string
  fullName: string | null
  crmName: string | null
  phone: string | null
  email: string | null
  avatarUrl: string | null
  jobTitle: string | null
  source: string | null
  lifecycleStage: string
  leadScore: number
  tags: string[]
  createdAt: string
  lastActivity: string | null
  aiSentimentLabel: string | null
  aiIntent: string | null
  assignedUser: { fullName: string | null } | null
  _count?: { conversations: number }
  /** Tổng chi tiêu / số đơn từ đơn hàng đã đồng bộ (chỉ có ở danh sách). */
  totalSpent?: number
  orderCount?: number
}

export interface ContactListResponse {
  contacts: ContactListItem[]
  total: number
  page: number
  limit: number
  /** Số khách theo giai đoạn (theo các bộ lọc khác giai đoạn). */
  stageCounts?: Record<string, number>
}

export interface ContactConversation {
  id: string
  displayName: string | null
  threadType: string | null
  lastMessageAt: string | null
  unreadCount: number
  channelAccount: { id: string; displayName: string | null } | null
  messages: { content: string | null; contentType: string | null; senderType: string | null; sentAt: string }[]
}

export interface ContactDetail extends ContactListItem {
  birthday?: string | null
  notes: string | null
  zaloUid: string | null
  assignedUserId: string | null
  aiSummary: string | null
  aiSentimentConfidence: number | null
  aiSentimentReason: string | null
  aiPainPoints: string[]
  aiCompetitors: string[]
  aiSignals: string[]
  aiAnalyzedAt: string | null
  conversations: ContactConversation[]
}

export interface ContactUpdateInput {
  fullName?: string
  phone?: string
  email?: string
  jobTitle?: string
  /** 'YYYY-MM-DD' hoặc null để xoá. */
  birthday?: string | null
  source?: string
  lifecycleStage?: string
  notes?: string
  tags?: string[]
}

// ── Query hooks ──────────────────────────────────────────────────────
export interface ContactQueryParams {
  page?: number
  limit?: number
  search?: string
  lifecycleStage?: string
  /** Chỉ lấy khách là bạn bè đã đồng bộ của tài khoản Zalo này. */
  channelAccountId?: string
  /** Tên nhãn (danh mục Cài đặt → Nhãn); nhiều nhãn cách dấu phẩy = có ít nhất một. */
  tag?: string
  assignedUserId?: string
  segmentId?: string
}

export function useContacts(params: ContactQueryParams) {
  return useApiQuery<ContactListResponse>(
    ['contacts', params],
    '/contacts',
    params as Record<string, unknown>,
    { placeholderData: (prev) => prev },
  )
}

export function useContact(id: string | undefined) {
  return useQuery<ContactDetail>({
    queryKey: ['contact', id],
    queryFn: async () => {
      const { data } = await api.get<ContactDetail>(`/contacts/${id}`)
      return data
    },
    enabled: !!id,
  })
}

// ── Mutation hooks ───────────────────────────────────────────────────
export function useUpdateContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; data: ContactUpdateInput }) => {
      const { data } = await api.put(`/contacts/${vars.id}`, vars.data)
      return data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['contact', vars.id] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

/** Gộp tay: `mergeIds` được dồn vào khách chính `primaryId` (POST /contacts/merge — theo từng trường, hoàn tác được). */
export function useMergeContacts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { primaryId: string; mergeIds: string[] }) => (await api.post('/contacts/merge', vars)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['contact'] })
      qc.invalidateQueries({ queryKey: ['contact-merges'] })
    },
  })
}

export function useCreateContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: ContactUpdateInput) => {
      const res = await api.post('/contacts', data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  })
}

export function useDeleteContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/contacts/${id}`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  })
}

// ── Ghi chú (Notes) ──────────────────────────────────────────────────
export interface Note {
  id: string
  content: string
  isPinned: boolean
  createdAt: string
  contactId: string | null
  createdBy: { id: string; fullName: string | null; avatarUrl: string | null } | null
  /** manual | legacy (ghi chú hồ sơ cũ) | import | pancake… — MỘT loại ghi chú, khác nhau ở nguồn. */
  source?: string
  sourceLabel?: string | null
  /** Thời điểm GỐC ở nền tảng nguồn (null = nguồn không có). */
  sourceAt?: string | null
  /** Thời điểm hiển thị do máy chủ tính (sourceAt ?? createdAt). */
  time?: string
}

export function useNotes(contactId: string | undefined) {
  return useQuery<{ notes: Note[]; total: number }>({
    queryKey: ['notes', { contactId }],
    queryFn: async () => {
      const { data } = await api.get('/notes', { params: { contactId } })
      return data
    },
    enabled: !!contactId,
  })
}

export function useCreateNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { contactId: string; content: string; isPinned?: boolean }) => {
      const { data } = await api.post('/notes', vars)
      return data
    },
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['notes', { contactId: vars.contactId }] }),
  })
}

export function useUpdateNote(contactId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; content?: string; isPinned?: boolean }) => {
      const { id, ...body } = vars
      const { data } = await api.put(`/notes/${id}`, body)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes', { contactId }] }),
  })
}

export function useToggleNotePin(contactId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.patch(`/notes/${id}/pin`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes', { contactId }] }),
  })
}

export function useDeleteNote(contactId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/notes/${id}`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes', { contactId }] }),
  })
}

// ── Lịch hẹn (Appointments) ──────────────────────────────────────────
export const APPOINTMENT_TYPE_LABELS: Record<string, string> = {
  get call() { return i18n.t('custList:hook.appointmentType.call') },
  get meeting() { return i18n.t('custList:hook.appointmentType.meeting') },
  demo: 'Demo',
  get visit() { return i18n.t('custList:hook.appointmentType.visit') },
  get other() { return i18n.t('custList:hook.appointmentType.other') },
}

export const APPOINTMENT_STATUS_LABELS: Record<string, string> = {
  get scheduled() { return i18n.t('custList:hook.appointmentStatus.scheduled') },
  get completed() { return i18n.t('custList:hook.appointmentStatus.completed') },
  get cancelled() { return i18n.t('custList:hook.appointmentStatus.cancelled') },
}

export function appointmentStatusVariant(status?: string | null): BadgeProps['variant'] {
  switch (status) {
    case 'completed':
      return 'success'
    case 'cancelled':
      return 'destructive'
    default:
      return 'default'
  }
}

export interface Appointment {
  id: string
  contactId: string | null
  appointmentDate: string
  appointmentTime: string | null
  type: string
  status: string
  notes: string | null
  contact?: { id: string; fullName: string | null; phone: string | null } | null
}

export function useAppointments(contactId: string | undefined) {
  return useQuery<{ appointments: Appointment[]; total: number }>({
    queryKey: ['appointments', { contactId }],
    queryFn: async () => {
      const { data } = await api.get('/appointments', { params: { contactId } })
      return data
    },
    enabled: !!contactId,
  })
}

export interface AppointmentInput {
  contactId?: string
  appointmentDate: string
  appointmentTime?: string
  type?: string
  status?: string
  notes?: string
}

export function useCreateAppointment(contactId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: AppointmentInput) => {
      const res = await api.post('/appointments', data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointments', { contactId }] }),
  })
}

export function useUpdateAppointment(contactId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; data: Partial<AppointmentInput> }) => {
      const res = await api.put(`/appointments/${vars.id}`, vars.data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointments', { contactId }] }),
  })
}

export function useDeleteAppointment(contactId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/appointments/${id}`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointments', { contactId }] }),
  })
}

// ── Trùng lặp (Duplicates) ───────────────────────────────────────────
export interface DuplicateContact {
  id: string
  fullName: string | null
  phone: string | null
  email: string | null
  avatarUrl: string | null
  source: string | null
  lifecycleStage: string
  zaloUid: string | null
  leadScore: number
  createdAt: string
  lastActivity: string | null
  _count?: { conversations: number }
}

export interface DuplicateGroup {
  id: string
  matchType: string
  confidence: number
  resolved: boolean
  contactIds: string[]
  createdAt: string
  contacts: DuplicateContact[]
}

export function useDuplicateGroups() {
  return useQuery<{ groups: DuplicateGroup[]; total: number }>({
    queryKey: ['duplicate-groups'],
    queryFn: async () => {
      const { data } = await api.get('/contacts/duplicates', { params: { limit: 100 } })
      return data
    },
  })
}

export function useScanDuplicates() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/contacts/duplicates/scan')
      return data as { ok: boolean; scanned: number; newGroups: number }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['duplicate-groups'] }),
  })
}

export function useMergeDuplicateGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { groupId: string; primaryContactId: string }) => {
      const { data } = await api.post(`/contacts/duplicates/${vars.groupId}/merge`, {
        primaryContactId: vars.primaryContactId,
      })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['duplicate-groups'] })
      qc.invalidateQueries({ queryKey: ['contact-merges'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export interface MergeLogItem {
  id: string
  primaryContactId: string
  primaryName: string
  mergedNames: string[]
  createdAt: string
  undoneAt: string | null
}

export function useMergeHistory() {
  return useQuery<{ merges: MergeLogItem[] }>({
    queryKey: ['contact-merges'],
    queryFn: async () => (await api.get('/contacts/merges')).data,
  })
}

export function useUndoMerge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (mergeLogId: string) => (await api.post(`/contacts/merges/${mergeLogId}/undo`)).data as { restored: number },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-merges'] })
      qc.invalidateQueries({ queryKey: ['duplicate-groups'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useDismissDuplicateGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (groupId: string) => {
      const { data } = await api.post(`/contacts/duplicates/${groupId}/dismiss`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['duplicate-groups'] }),
  })
}

// ── Trích số điện thoại ──────────────────────────────────────────────
export interface PhoneMatch {
  id: string
  fullName: string
  extractedPhone: string
}

export interface ExtractPhonesResult {
  ok: boolean
  dryRun: boolean
  scanned: number
  matched: number
  updated: number
  matches?: PhoneMatch[]
  /** Số trích được nhưng đã thuộc khách khác — không gán, nên gộp hai khách thay vì tạo trùng. */
  conflicts?: (PhoneMatch & { existingContactId: string })[]
}

export function useExtractPhones() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { dryRun: boolean; excludeIds?: string[] }) => {
      const { data } = await api.post('/contacts/extract-phones', vars)
      return data as ExtractPhonesResult
    },
    onSuccess: (res) => {
      if (!res.dryRun) qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

// ── Nhập khách hàng từ file ──────────────────────────────────────────
export interface ContactImportOptions {
  dedupeByPhone: boolean
  dedupeByEmail: boolean
  onDuplicate: 'skip' | 'update'
  extraTag?: string
  assignedUserId?: string
  runAutomation: boolean
}

export type ImportRowStatus = 'create' | 'update' | 'skip' | 'error'

export interface ImportRowResult {
  rowNumber: number
  status: ImportRowStatus
  message?: string
  contactId?: string
  fullName?: string | null
  phone?: string | null
  email?: string | null
}

export interface ContactImportResponse {
  summary: { total: number; created: number; updated: number; skipped: number; errors: number }
  /** Nhập: mọi dòng. Xem trước: 20 dòng đầu. */
  results: ImportRowResult[]
  /** Chỉ ở xem trước: dòng lỗi / bỏ qua. */
  problems?: ImportRowResult[]
}

export interface ContactImportPayload {
  rows: ImportRowInput[]
  options: ContactImportOptions
}

export function useContactImportPreview() {
  return useMutation({
    mutationFn: async (payload: ContactImportPayload) => {
      const { data } = await api.post<ContactImportResponse>('/contacts/import/preview', payload)
      return data
    },
  })
}

export function useContactImport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ContactImportPayload) => {
      // 10.000 dòng có thể mất vài phút — không dùng timeout mặc định.
      const { data } = await api.post<ContactImportResponse>('/contacts/import', payload, { timeout: 15 * 60_000 })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  })
}

// ── Xuất CSV ─────────────────────────────────────────────────────────
/** Tải CSV qua axios (kèm Bearer token) rồi lưu file — không đưa token lên URL. */
async function downloadBlob(path: string, params: Record<string, unknown>, filename: string): Promise<boolean> {
  const res = await api.get<Blob>(path, { params, responseType: 'blob' })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  // Server cắt ở 100.000 dòng và báo qua header — giao diện nhắc người dùng lọc hẹp.
  return res.headers?.['x-export-truncated'] === '1'
}

export interface ContactExportFilters {
  search?: string
  lifecycleStage?: string
  channelAccountId?: string
  tag?: string
  assignedUserId?: string
  segmentId?: string
}

/** Xuất danh sách khách theo đúng bộ lọc đang xem. Trả true nếu bị cắt ở giới hạn. */
export const downloadContactsCsv = (f: ContactExportFilters) => downloadBlob('/contacts/export', { ...f }, 'khach-hang.csv')

/** Xuất khách thuộc một phân khúc. Trả true nếu bị cắt ở giới hạn. */
export const downloadSegmentCsv = (segmentId: string) => downloadBlob(`/cdp/segments/${segmentId}/export`, {}, 'phan-khuc.csv')

// ── Thao tác hàng loạt ───────────────────────────────────────────────
export type BulkContactBody =
  | { action: 'add_tags' | 'remove_tags'; ids: string[]; tags: string[] }
  | { action: 'set_stage'; ids: string[]; stage: string }
  | { action: 'assign'; ids: string[]; assignedUserId: string | null }

export function useBulkContacts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: BulkContactBody) =>
      (await api.post<{ ok: boolean; selected: number; matched: number; changed: number; skippedMissing?: number; skippedMissingFields?: string[] }>('/contacts/bulk', body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['contact'] })
      qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}
